import { useCallback, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _ from "lodash"

import {
  Q_PARENT_CATEGORY_ID,
  Q_VENUE,
  PROMOTION_TAXONOMY,
  DEFAULT_FILTER_VALUE,
  DEFAULT_PROMOTION_FILTER_VALUE,
  Q_PROMOTION_GROUP,
  PROMOTION_PAGE_CONFIG,
} from "constant"

import { useVenue } from "providers/venue"
import { useGetPageConfig } from "providers/venue/modules/data"
import { useGeoLocation } from "providers/geolocation"
import { useVenueController } from "hooks/venue/useVenueController"
import { useRecommendedPromotion } from "hooks/sponsored-content/recommended"
import { filterCurrentFeature, filterUpcomingFeature } from "utils/feature"
import { useGetList } from "venue-react"

export const usePromotion = () => {
  const { data: allPromotions = [] } = useGetList("promotion", {
    populate: true,
  })
  const { venues, isMultiVenueProject, taxonomies = [] } = useVenue()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { recommendedPromotions } = useRecommendedPromotion()
  const { layout = "carousel", metadata } = useGetPageConfig(
    PROMOTION_PAGE_CONFIG
  )

  const promotionCategoryFilter = useMemo(
    () => metadata?.filter?.category || "store",
    [metadata]
  )

  const promotionDisplayMode = useMemo(
    () => metadata?.displayMode || "all",
    [metadata]
  )

  // Query String filters
  const activeGroupPromotion = useMemo(
    () => searchParams.get(Q_PROMOTION_GROUP) || DEFAULT_PROMOTION_FILTER_VALUE,
    [searchParams]
  )

  // Query String filters
  const parentCategoryId = useMemo(
    () => searchParams.get(Q_PARENT_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const promotionCategories = useMemo(
    () =>
      taxonomies.filter(
        ({ properties }) => properties.taxonomy === PROMOTION_TAXONOMY
      ),
    [taxonomies]
  )

  const showCategoryFilter = useMemo(
    () => promotionCategories.length > 0,
    [promotionCategories]
  )

  const changeVenueFilter = useCallback(
    (venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)
      navigate(`/promotions/?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleChangeParentCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_PARENT_CATEGORY_ID, id)
      navigate(`/promotions?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleChange = useCallback(
    (e, promotionGroup) => {
      if (promotionGroup === null) return
      searchParams.set(Q_PROMOTION_GROUP, promotionGroup)
      navigate(`/promotions?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const basedPromotions = useMemo(
    () =>
      allPromotions?.filter(
        ({ properties }) =>
          promotionCategoryFilter === "all" ||
          properties.category === promotionCategoryFilter
      ),
    [allPromotions, promotionCategoryFilter]
  )

  const promotionsInParentCategory = useMemo(() => {
    return basedPromotions.filter(({ properties }) => {
      const categories = _(properties).get("local_parent_categories", [])
      const isMatchedCategoryId = _(categories)
        .map("id")
        .includes(parentCategoryId)
      return (
        parentCategoryId === DEFAULT_FILTER_VALUE ||
        (parentCategoryId && isMatchedCategoryId)
      )
    })
  }, [basedPromotions, parentCategoryId])

  const promotionsInViewingVenue = useMemo(
    () =>
      basedPromotions?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties.venue_id === venueFilter ||
          !properties.venue_id
      ),
    [basedPromotions, venueFilter]
  )

  const filteredPromotions = useMemo(() => {
    const recommendedStorePromotions = _.intersectionBy(
      recommendedPromotions.data,
      promotionsInViewingVenue,
      "id"
    )
    const basedPromotions = _.intersectionBy(
      promotionsInParentCategory,
      promotionsInViewingVenue,
      "id"
    )

    const sanitizedPromotions = _.uniqBy(
      _.compact([...recommendedStorePromotions, ...basedPromotions]),
      "id"
    )
    return sanitizedPromotions
  }, [
    recommendedPromotions,
    promotionsInViewingVenue,
    promotionsInParentCategory,
  ])

  const currentPromotions = useMemo(
    () => filteredPromotions.filter(filterCurrentFeature),
    [filteredPromotions]
  )

  const upcomingPromotions = useMemo(
    () => filteredPromotions.filter(filterUpcomingFeature),
    [filteredPromotions]
  )

  const matchedRecommendedPromotions = useMemo(
    () =>
      _.intersectionBy(
        recommendedPromotions.data,
        promotionsInViewingVenue,
        "id"
      ),
    [recommendedPromotions, promotionsInViewingVenue]
  )

  const currentRecommendedPromotions = useMemo(
    () => matchedRecommendedPromotions.filter(filterCurrentFeature),
    [matchedRecommendedPromotions]
  )

  const upcomingRecommendedPromotions = useMemo(
    () => matchedRecommendedPromotions.filter(filterUpcomingFeature),
    [matchedRecommendedPromotions]
  )

  const groupPromotions = useMemo(
    () => ({
      current: currentPromotions,
      upcoming: upcomingPromotions,
    }),
    [currentPromotions, upcomingPromotions]
  )

  // Add All Promotion for new layout (current + upcoming) and can support old design (separated current/upcoming)
  const promotions = useMemo(
    () => [
      ...currentRecommendedPromotions,
      ...upcomingRecommendedPromotions,
      ...currentPromotions,
      ...upcomingPromotions,
    ],
    [
      currentRecommendedPromotions,
      upcomingRecommendedPromotions,
      currentPromotions,
      upcomingPromotions,
    ]
  )

  const sanitizedAllPromotions = useMemo(
    () => _.uniqBy(promotions, "id"),
    [promotions]
  )

  return {
    parentCategoryId,
    promotionCategories,
    basedPromotions,
    filteredPromotions,
    locationVenueId,
    venueFilter,
    venues,
    isMultiVenueProject,
    showCategoryFilter,
    groupPromotions,
    activeGroupPromotion,
    promotions: sanitizedAllPromotions,
    layout,
    displayMode: promotionDisplayMode,
    changeVenueFilter,
    handleChangeParentCategory,
    handleChange,
  }
}
